"use strict";
/**
 * Question set **v2** of the onboarding questionnaire — the set the standalone onboarding
 * wizard renders. It lives beside v1 (`BRAND_STATUS_QUO_QUESTIONS`) rather than replacing it:
 * the chat-based onboarding flow, the LLM backfill, and every already-stored v1 response keep
 * resolving against v1 forever. Nothing here mutates v1.
 *
 * Question and option ids are canonical and persisted verbatim, so they are stable API even
 * though the wording is not. Two sets can therefore coexist in the same table; the row's
 * `version` says which one to read it with (see `questionSetForVersion`).
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.BRAND_STATUS_QUO_QUESTIONS_V2 = exports.BRAND_STATUS_QUO_QUESTION_SET_VERSION_V2 = void 0;
exports.questionSetForVersion = questionSetForVersion;
const brand_status_quo_onboarding_1 = require("./brand-status-quo-onboarding");
/**
 * Version stamped on every v2 response payload. Paired with
 * `BRAND_STATUS_QUO_QUESTION_SET_VERSION` (= 1) in `brand-status-quo-response.ts`; a reader
 * resolves a stored payload's question set from its `version` field alone.
 */
exports.BRAND_STATUS_QUO_QUESTION_SET_VERSION_V2 = 2;
exports.BRAND_STATUS_QUO_QUESTIONS_V2 = [
    {
        id: 'role',
        title: 'Which of these describes you best?',
        select: 'single',
        options: [
            { id: 'own_brand', label: 'I run my own brand(s)' },
            { id: 'in_house', label: "I'm on an in-house marketing team" },
            { id: 'agency', label: 'I run an agency or manage brands for clients' },
        ],
    },
    {
        id: 'brands',
        title: 'How many brands are you running?',
        select: 'single',
        options: [
            { id: 'one', label: 'Just one' },
            { id: 'b2_5', label: '2–5' },
            { id: 'b6_15', label: '6–15' },
            { id: 'b16_40', label: '16–40' },
            { id: 'b40p', label: '40+' },
        ],
    },
    {
        id: 'tools',
        title: 'Which of these do you already use?',
        subtitle: 'Select all that apply',
        select: 'multi',
        skippable: true,
        options: [
            { id: 'meta_ads', label: 'Meta Ads' },
            { id: 'google_ads', label: 'Google Ads' },
            { id: 'klaviyo', label: 'Klaviyo' },
            { id: 'hubspot', label: 'Hubspot' },
            { id: 'google_analytics', label: 'Google Analytics' },
            { id: 'chatgpt', label: 'ChatGPT' },
            { id: 'claude', label: 'Claude' },
        ],
    },
    {
        id: 'spend',
        title: "What's your monthly ad spend?",
        select: 'single',
        options: [
            { id: 'none', label: 'Not running paid ads yet' },
            { id: 's_u1', label: 'Under $1k' },
            { id: 's1_10', label: '$1–10k' },
            { id: 's10_50', label: '$10–50k' },
            { id: 's50p', label: '$50k+' },
        ],
    },
    {
        id: 'goal',
        title: 'What are you trying to move right now?',
        select: 'single',
        allowOther: true,
        otherLabel: 'Other',
        options: [
            { id: 'launch_first_ads', label: 'Launch my first ads' },
            { id: 'whats_working', label: 'Find out what is working' },
            { id: 'competitors', label: 'Learn about my competitors' },
            { id: 'scale_creatives', label: 'Scale my marketing with more creatives' },
            { id: 'buy_again', label: 'Get people buying again' },
        ],
    },
];
/**
 * The question set a given response version was captured with. Unknown versions fall back to v1,
 * matching the endpoint's existing permissive stale-client policy: a body whose version we do not
 * recognise is still recorded (never rejected), normalized against the closest known set.
 */
function questionSetForVersion(version) {
    return version === exports.BRAND_STATUS_QUO_QUESTION_SET_VERSION_V2
        ? exports.BRAND_STATUS_QUO_QUESTIONS_V2
        : brand_status_quo_onboarding_1.BRAND_STATUS_QUO_QUESTIONS;
}
